import { useRef } from "react";
import { useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Flag, HeartHandshake, ShieldCheck } from "lucide-react";
import brand from "@/config/brand";
import { MaskedLine, FadeIn } from "@/components/Reveal";
import DeviceMockup from "@/components/DeviceMockup";
import { useInquiry } from "@/components/InquiryModal";
import { scrollToId } from "@/lib/scroll";

const BENEFITS = [
  { icon: Flag, label: "Proudly U.S.-Based", id: "us-based" },
  { icon: HeartHandshake, label: "Personalized Service", id: "personalized" },
  { icon: ShieldCheck, label: "Ongoing Website Care", id: "ongoing-care" },
];

const Hero = () => {
  const { open } = useInquiry();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 60, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      onMouseMove={onMove}
      className="relative bg-cream overflow-hidden pt-[72px]"
    >
      <div className="noise-overlay" aria-hidden="true" />
      <div
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-champagne/30 blur-[140px] pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 md:pt-24 md:pb-32 lg:pt-28 lg:pb-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <FadeIn delay={0.1}>
              <p
                data-testid="hero-eyebrow"
                className="text-xs font-sans uppercase tracking-[0.28em] font-semibold text-turquoise-deep mb-8 flex items-center gap-3"
              >
                <span className="w-10 h-px bg-turquoise inline-block" />
                Boutique Web Design · Hosting · Care
              </p>
            </FadeIn>

            <h1
              data-testid="hero-headline"
              className="font-serif font-medium tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.08]"
            >
              <MaskedLine delay={0.25}>Beautiful Websites.</MaskedLine>
              <MaskedLine delay={0.4}>
                <span className="italic text-turquoise">Effortlessly Done.</span>
              </MaskedLine>
            </h1>

            <FadeIn delay={0.7}>
              <p
                data-testid="hero-subhead"
                className="mt-7 text-lg sm:text-xl font-sans text-charcoal/85 leading-relaxed max-w-xl"
              >
                {brand.subhead}
              </p>
              <p className="mt-4 text-base font-sans text-taupe leading-relaxed max-w-xl">
                {brand.intro}
              </p>
            </FadeIn>

            <FadeIn delay={0.9} className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-get-website-button"
                onClick={open}
                className="group bg-turquoise text-charcoal hover:bg-turquoise-dark px-9 py-4 rounded-full font-sans font-semibold text-base shadow-[0_18px_36px_-12px_rgba(0,175,163,0.5)] hover:shadow-[0_24px_44px_-12px_rgba(0,175,163,0.6)] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                Get My Website
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-view-work-button"
                onClick={() => scrollToId("work")}
                className="border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-cream px-9 py-4 rounded-full font-sans font-medium text-base transition-colors duration-300"
              >
                View Our Work
              </button>
            </FadeIn>

            <FadeIn delay={1.12} className="mt-9">
              <div data-testid="hero-benefits-row" className="flex flex-col sm:grid sm:grid-cols-3 gap-y-3.5">
                {BENEFITS.map((b, i) => (
                  <span
                    key={b.id}
                    data-testid={`hero-benefit-${b.id}`}
                    className={`flex items-center gap-2 ${
                      i > 0 ? "sm:border-l sm:border-taupe/30 sm:pl-5 sm:ml-1" : "sm:pr-1"
                    }`}
                  >
                    <b.icon size={16} className="text-turquoise-deep shrink-0" strokeWidth={1.75} />
                    <span className="text-[11px] font-sans font-semibold uppercase tracking-[0.12em] text-charcoal/70 whitespace-nowrap">
                      {b.label}
                    </span>
                  </span>
                ))}
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-6 lg:pl-8 pb-10">
            <DeviceMockup rotateX={rotateX} rotateY={rotateY} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
